import Dec from 'decimal.js';
import { getErc20Contract as _ } from './contractsRegistryService';
import { ASSETS, DEFAULT_ASSET } from '../constants/assets';
import callTx from './txService';
import { ethToWei, WeiToEth } from './web3Service';
import { CREATE_HPOOL_REQUIRED_TOKENS } from '../constants/createHPool';
import { getContractAddress, getContractInstance, useContractReader } from './contractReader';
import { getValueByMultiProps, insert } from '../utils/mapper';

const getErc20Contract = (address) => getContractInstance('Hpool', address);

/**
 * Gets full asset data for the asset symbol
 * @param assetSymbol {String}
 * @return {{symbol: string, address: *, decimals: number, name: string, icon: *}|void|{symbol: string, address: string, decimals: number, name: string, icon: function(): string}}
 */
export const getAssetInfo = (assetSymbol) =>
  ASSETS.find((t) => t.symbol.toLowerCase() === assetSymbol?.toLowerCase()) ||
  console.trace(`Asset "${assetSymbol}" not found `) || { ...DEFAULT_ASSET };

export const mergeToken = (token) => {
  const feToken = CREATE_HPOOL_REQUIRED_TOKENS[token.symbol.toLowerCase()];
  return feToken ? { ...feToken, ...token } : token;
};

const getMultiProp = (token, key) => getValueByMultiProps(token, [`token_${key}`, key]);

export const resetAssets = (assets) =>
  assets.filter((token) => !!CREATE_HPOOL_REQUIRED_TOKENS[getMultiProp(token, 'symbol').toLowerCase()]).map(mergeToken);

/**
 * Gets the assets number of decimals and does the conversion to eth
 * @param amount {Number, String, Object}
 * @param assetSymbol {String}
 * @return {String}
 */
export const assetAmountInEth = (amount = 0, assetSymbol = 'ETH') => {
  const { decimals } = getAssetInfo(assetSymbol);
  return Dec(amount?.toString() || 0)
    .div(10 ** decimals)
    .toString();
};

export const safeDivideOnWei = (_number = '', decimals = 18) => {
  // move a dot {decimals} times from the right to the left of string
  const numberLength = _number.length;

  // handle small wei numbers
  const numOfZeroDigits = decimals - numberLength >= 0 ? decimals - numberLength : 0;
  const zerosString = new Array(numOfZeroDigits).fill('0').join('');
  const prevString = (!!numOfZeroDigits && '0') || '';
  const dividedOnWei = insert(_number, numberLength - 18, [prevString, zerosString].join('.'));
  return dividedOnWei;
};

export const safeAmountInWei = (number = '', decimals = 18) => {
  const _number = [number, ''].join();
  if (_number.length < decimals || +_number < 0) return '0';

  const dotPosition = _number.indexOf('.');

  const [, howManyDigitsRightToDot] = _number.split('.');
  const rightDigitsLength = howManyDigitsRightToDot.replace(',', '').length;

  const _numOfZeroDigits = decimals - rightDigitsLength >= 0 ? decimals - rightDigitsLength : 0;
  const _zerosString = new Array(_numOfZeroDigits).fill('0').join('');

  const mergedWithZeroString = [_number, _zerosString].join('').replace('.', '');
  const showDot = rightDigitsLength > decimals;

  const afterMovingDot = showDot ? insert(mergedWithZeroString, dotPosition + decimals, '.') : mergedWithZeroString;
  return afterMovingDot.replace(',', '');
};

export const formatLargeNumber = (num = '', digits = 3) => {
  const parsed = +num;
  return parsed?.toFixed(digits);
};

/**
 * Gets the assets number of decimals and does the conversion to wei
 * @param amount {Number, String, Object}
 * @param assetSymbol {String}
 * @return {String}
 */
export const assetAmountInWei = (amount = 0, passedDecimals = 18) =>
  Dec(amount?.toString() || 0)
    .mul(10 ** passedDecimals)
    .floor()
    .toFixed()
    .toString();
/**
 * Fetches the allowance amount on a asset
 *
 * @param assetSymbol {String} Token symbol
 * @param owner {String}
 * @param spender {String}
 * @return {Promise<string>}
 */
const getAssetAllowanceForAddress = async (assetSymbol, owner, spender) => {
  const contract = await getErc20Contract(getAssetInfo(assetSymbol).address);
  const data = await contract.methods.allowance(owner, spender).call();

  return assetAmountInEth(data, assetSymbol);
};

/**
 * Transforms the approve type to the prop name in the reducer for when
 * an asset is being approved
 *
 * @param approveType
 * @return {string}
 */
export const getApprovingReducerPropName = (approveType) => `approving${approveType}`;

/**
 * Transforms the approve type to the prop name in the reducer when
 * checking if an asset is approved
 *
 * @param approveType
 * @return {string}
 */
export const getGettingIsApprovedReducerPropName = (approveType) => `gettingIsApproved${approveType}`;

/**
 * Transforms the approve type to the prop name in the reducer when
 * checking if an asset is approved
 *
 * @param approveType
 * @return {string}
 */
export const getIsApprovedReducerPropName = (approveType) => `approved${approveType}`;

/**
 * Gets from the state if the approving tx
 * for an account is sending
 *
 * @param state {Object}
 * @param assetSymbol {String}
 * @param approveType {String}
 * @returns {*|boolean}
 */
export const getApprovingState = (state, assetSymbol, approveType) => {
  const { wallet, assets } = state;
  const { account } = wallet;

  if (!account) return false;

  const singleAssetAccountState = assets[assetSymbol][account];
  if (!singleAssetAccountState) return false;

  const propName = getApprovingReducerPropName(approveType);

  return singleAssetAccountState[propName] || false;
};

/**
 * Checks if an asset is approved for some amount
 *
 * @param addressToCheck {String}
 * @param account {String}
 * @param assetSymbol {String}
 * @param amount {String}
 * @return {Promise<boolean>}
 */
export const isAddressApprovedOnAsset = async (assetSymbol, account, addressToCheck, amount) => {
  if (assetSymbol === 'ETH') return true;

  const allowance = await getAssetAllowanceForAddress(assetSymbol, account, addressToCheck);
  return Dec(allowance).gte(amount);
};

/**
 * Approves Erc20 asset for an address
 *
 * @param assetSymbol {String} Token symbol
 * @param from {String}
 * @param approveAddress {String} Spender address
 * @return {Promise<Boolean>}
 */

export const getAssetsContract = async (address) => getErc20Contract(address);

export const approveAddressOnAsset = async (from, approveAddress, passedNum, hasAllowedSpending) => {
  if (hasAllowedSpending) return new Promise((res, rej) => res(true));
  const hordAddress = await getContractAddress('HordToken');
  const assetContract = await getErc20Contract(hordAddress);
  const num = BigInt(passedNum).toString() || ethToWei(Number.MAX_SAFE_INTEGER.toString());

  return callTx(assetContract, 'approve', [approveAddress, num], { from });
};

/**
 * Gets balance of a provided address on an Erc20 asset
 *
 * @param assetSymbol {String}
 * @param address {String}
 * @return {Promise<String>}
 */
export const getAssetBalance = async (assetSymbol, address) => {
  let data = '';

  if (assetSymbol.toLocaleLowerCase() !== 'hord') data = await window._web3.eth.getBalance(address);
  else {
    try {
      const hordBalance = await useContractReader('HordToken', 'balanceOf', [address]);
      data = hordBalance;
    } catch (error) {
      // console.log({ error });
    }
  }

  // This maybe needs to be changed if we are checking
  // the balance of one token on another token
  return assetAmountInEth(data, assetSymbol);
};

/**
 * Fetches total supply of any asset
 * @param assetSymbol {String}
 * @returns {Promise<String>}
 */
export const getAssetTotalSupply = async (assetSymbol) => {
  const contract = await getErc20Contract(getAssetInfo(assetSymbol).address);
  const data = (await contract.methods.totalSupply().call()).toString();
  return assetAmountInEth(data, assetSymbol);
};

export const createUniswapSwapLink = (assetSymbol) =>
  `https://app.uniswap.org/#/swap?outputCurrency=${getAssetInfo(assetSymbol).address}`;

export const getAssetPrice = (asset, currency) => {
  if (asset === 'ETH' && currency === 'USD') return '2000';
  return '0';
};

export const getCointConvertUrl = (base = 'ethereum', vs = 'usd') =>
  `https://api.coingecko.com/api/v3/simple/price?ids=${base}&vs_currencies=${vs}`;

const coinConverter = async (base, vs) => {
  const url = getCointConvertUrl(base, vs);
  const rawResponse = await fetch(url);
  const response = await rawResponse.json();
  return response;
};

export const convertCoins = (base, vs) => {
  const value = base / vs || 0.0;
  const selectedDigits = 4;
  return +value.toFixed(selectedDigits);
};

export const getExchangeValue = (exhangeRes, base, vs) => exhangeRes?.[base]?.[vs];

export const fromUsdToEth = async (inUsd) => {
  const converRes = await coinConverter();
  const base = 'ethereum';
  const vs = 'usd';
  const ethValue = converRes?.[base]?.[vs];
  if (!ethValue) return;
  const value = inUsd / ethValue || 0.0;
  const selectedDigits = 4;
  return +value.toFixed(selectedDigits);
};

export const getBusdBalance = async (address) => {
  try {
    const busdBalance = await useContractReader('BUSD', 'balanceOf', [address]);

    return WeiToEth(busdBalance);
  } catch (error) {
    console.log({ error });
  }
};
